import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import {
  evaluatePermissionAccess,
  normalizePermissionAccess,
} from '../auth/permission-access.util';
import { PermissionDebugLoggerService } from '../auth/permission-debug-logger.service';
import { SessionService } from '../auth/session.service';

function normalizePermissions(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
}

export const permissionGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const session = inject(SessionService);
  const debugLogger = inject(PermissionDebugLoggerService);

  const me = session.getCurrentUser();
  const userPermissions = me?.permissions ?? [];
  const effectivePermissions = new Set(userPermissions);

  const rawAccess = route.data?.['access'];
  if (rawAccess !== undefined) {
    const accessNode = normalizePermissionAccess(rawAccess);
    const allowed = !!accessNode && evaluatePermissionAccess(accessNode, effectivePermissions);
    debugLogger.log({
      route,
      allowed,
      mode: 'access',
      required: rawAccess,
      userPermissions,
    });
    if (!allowed) {
      return router.createUrlTree(['/403']);
    }
    return true;
  }

  const requiredPermissions = normalizePermissions(route.data?.['permissions']);
  if (requiredPermissions.length === 0) {
    debugLogger.log({
      route,
      allowed: true,
      mode: 'permissions',
      required: [],
      userPermissions,
    });
    return true;
  }

  const allowed = requiredPermissions.every((permission) => effectivePermissions.has(permission));
  debugLogger.log({
    route,
    allowed,
    mode: 'permissions',
    required: requiredPermissions,
    userPermissions,
  });
  if (allowed) {
    return true;
  }

  return router.createUrlTree(['/403']);
};
